import {
  PUBLIC_PRICING_FALLBACK,
  creativesPerMonth,
  formatMoney,
  type PublicPlan,
} from '@krevya/shared';

/**
 * The arithmetic behind the landing page's price cards, kept out of the
 * component so it can be checked without a browser.
 *
 * Everything here is display: the amounts arrive in minor units from the
 * public-plans endpoint, and what is charged is whatever Whop says.
 */

export type Period = 'month' | 'year';

const isPrice = (v: unknown): v is { amount: number; currency: string } => {
  if (typeof v !== 'object' || v === null) return false;
  const price = v as Record<string, unknown>;
  return (
    typeof price['amount'] === 'number' &&
    Number.isFinite(price['amount']) &&
    price['amount'] > 0 &&
    typeof price['currency'] === 'string' &&
    /^[A-Za-z]{3}$/.test(price['currency'])
  );
};

/**
 * Whether an answer is the contract this page was written against.
 *
 * It must name the same plans in the same order as the fallback: a card is
 * built from the plan's id, so a plan this page has never heard of, or one
 * that went missing, is a mismatch to be ignored and not a card to guess at.
 */
export function isPublicPricing(body: unknown): body is { plans: PublicPlan[] } {
  if (typeof body !== 'object' || body === null) return false;
  const plans = (body as Record<string, unknown>)['plans'];
  if (!Array.isArray(plans) || plans.length !== PUBLIC_PRICING_FALLBACK.length) return false;
  // One answer, one currency: a card in lira beside one in dollars would be
  // compared as if they were the same money. The answer's own `currency`, when
  // it names one, is the one every price must be in.
  const named = (body as Record<string, unknown>)['currency'];
  const currencies = new Set<string>();
  if (typeof named === 'string') currencies.add(named.toLowerCase());
  for (const raw of plans as unknown[]) {
    const plan = (raw ?? {}) as Record<string, unknown>;
    for (const price of [plan['monthly'], plan['yearly']]) {
      if (isPrice(price)) currencies.add(price.currency.toLowerCase());
    }
  }
  if (currencies.size > 1) return false;
  return plans.every((raw: unknown, i) => {
    if (typeof raw !== 'object' || raw === null) return false;
    const plan = raw as Record<string, unknown>;
    const savings = plan['savingsPercent'];
    return (
      plan['id'] === PUBLIC_PRICING_FALLBACK[i]?.id &&
      typeof plan['name'] === 'string' &&
      plan['name'] !== '' &&
      typeof plan['credits'] === 'number' &&
      Number.isInteger(plan['credits']) &&
      plan['credits'] > 0 &&
      isPrice(plan['monthly']) &&
      (plan['yearly'] === null || isPrice(plan['yearly'])) &&
      (savings === null || (typeof savings === 'number' && Number.isFinite(savings))) &&
      typeof plan['selfServe'] === 'boolean' &&
      (plan['storeLimit'] === null ||
        (typeof plan['storeLimit'] === 'number' &&
          Number.isInteger(plan['storeLimit']) &&
          plan['storeLimit'] > 0))
    );
  });
}

/**
 * The yearly switch only exists when every plan that can be bought has a
 * yearly price to show. A plan arranged by agreement (Partner) is monthly by
 * design and does not hold the switch back.
 */
export const yearlyOffered = (plans: readonly PublicPlan[]) =>
  plans.filter((p) => p.selfServe).every((p) => p.yearly !== null);

function intlTag(formatLocale: string): string {
  return formatLocale.includes('-') ? formatLocale : formatLocale === 'tr' ? 'tr-TR' : 'en-US';
}

/**
 * Whole amounts without cents ($69, ₺2.399), the rest with them ($57.50).
 * In the reader's locale and with the currency's own sign, so an English page
 * shows ₺999 rather than "TRY 999".
 */
export function money(
  minor: number,
  currency: string,
  formatLocale: string,
  digits = minor % 100 === 0 ? 0 : 2,
): string {
  return formatMoney(minor, currency, intlTag(formatLocale), digits);
}

/**
 * What one credit costs, to the cent, in the reader's locale ("$0.07",
 * "₺2,50"). The same helper the billing page prints it with, so a visitor who
 * signs up reads the same figure inside the product that sold it to them.
 */
export function perCredit(
  minor: number,
  credits: number,
  currency: string,
  formatLocale = 'en-US',
): string {
  return formatMoney(minor / credits, currency, intlTag(formatLocale));
}

/**
 * The saving the yearly switch advertises. Every plan carries its own figure;
 * if they ever differ the smallest is shown, so the chip never promises more
 * than the worst card delivers.
 */
export function yearlySavings(plans: readonly PublicPlan[]): number | null {
  const all = plans.filter((p) => p.selfServe).map((p) => p.savingsPercent);
  if (all.length === 0 || all.some((v) => v === null || v <= 0)) return null;
  return Math.min(...(all as number[]));
}

export function planFigures(plan: PublicPlan, period: Period, formatLocale: string) {
  const tag = intlTag(formatLocale);
  const yearly = period === 'year' ? plan.yearly : null;
  const price = yearly ?? plan.monthly;
  // Credits arrive every month on either interval, so the allowance shown is
  // always the monthly one; only the price is spread over twelve of them.
  const priced = yearly ? plan.credits * 12 : plan.credits;
  return {
    headline: money(price.amount, price.currency, formatLocale),
    credits: plan.credits.toLocaleString(tag),
    yearCredits: (plan.credits * 12).toLocaleString(tag),
    images: creativesPerMonth(plan.credits).toLocaleString(tag),
    perCredit: perCredit(price.amount, priced, price.currency, formatLocale),
    perDay: money(Math.round(plan.monthly.amount / 30), plan.monthly.currency, formatLocale, 2),
    perMonth: plan.yearly
      ? money(Math.round(plan.yearly.amount / 12), plan.yearly.currency, formatLocale, 2)
      : null,
  };
}
